import { useEffect, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { cn } from "@/lib/utils";

/**
 * LA LISTA QUE SALE DEBAJO DEL CAMPO MIENTRAS SE ELIGE CULTIVO O VARIEDAD
 * (plan cultivos SIEX F3, 01-10-2026).
 *
 * EN EL FLUJO, NO FLOTANDO: empuja lo de debajo como cualquier otro contenido.
 * Un Popover anclado a un campo que está justo encima del teclado del teléfono,
 * dentro de una hoja con su propio scroll, se reposiciona al abrirse el teclado,
 * se recorta contra el `overflow-hidden` de la hoja y compite por el gesto de
 * arrastre (la misma razón que `RegistrySuggestions`).
 *
 * SIN TOPE NI SCROLL PROPIO: como mucho ~8 filas más las fijas, y quien se
 * desplaza es la hoja. Por eso tampoco se usa el `Command` de cmdk: mueve la
 * fila activa con `scrollIntoView`, que arrastra también las cajas con
 * `overflow: hidden` de arriba y deja la cabecera de la hoja fuera de la
 * pantalla (patrones-ui, enmienda del 27-09). Las flechas, Enter y Escape los
 * maneja el campo (`teclaLista`) con `aria-activedescendant`: el foco no sale
 * nunca del campo y el teclado del móvil no se cierra.
 *
 * ESTE COMPONENTE NO BUSCA, solo pinta lo que le dan ya buscado en memoria.
 *
 * Filas de 44 px como mínimo (se tocan con el dedo, a veces con guantes), y el
 * `onMouseDown` con `preventDefault` para que tocar una fila no le quite el
 * foco al campo antes del clic.
 */

export interface OpcionLista {
  key: string;
  contenido: ReactNode;
  onElegir: () => void;
  /** Las fijas («Sin variedad», «Otra variedad…») van separadas y en otro tono. */
  fija?: boolean;
  testId?: string;
}

interface ListaEnLineaProps {
  /** id del listbox; cada fila es `${id}-${i}` (para `aria-activedescendant`). */
  id: string;
  opciones: OpcionLista[];
  activa: number;
  onActiva: (i: number) => void;
  /** Se está trayendo el catálogo: hueco en vez de filas (nunca un aro que gira). */
  cargando?: boolean;
  /** Lo que va encima de las filas: «Ningún cultivo con…» cuando debajo solo queda «Usar «X» como cultivo propio». */
  cabecera?: ReactNode;
  /** Lo que va debajo de las filas: «Ningún cultivo con…», el origen de los datos. */
  pie?: ReactNode;
  className?: string;
  "aria-label"?: string;
}

export function opcionId(listaId: string, i: number): string {
  return `${listaId}-${i}`;
}

export function ListaEnLinea({ id, opciones, activa, onActiva, cargando = false, cabecera, pie, className, ...rest }: ListaEnLineaProps) {
  return (
    <div className={cn("mt-1.5 min-w-0 rounded-xl border border-[#b6dded] bg-white p-1 shadow-sm", className)} data-testid={`${id}-lista`}>
      {cargando && (
        <div className="space-y-1 p-1" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      )}
      {cabecera}
      <div role="listbox" id={id} aria-label={rest["aria-label"]}>
        {opciones.map((o, i) => (
          <button
            key={o.key}
            id={opcionId(id, i)}
            type="button"
            role="option"
            tabIndex={-1}
            aria-selected={i === activa}
            data-testid={o.testId}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => onActiva(i)}
            onClick={o.onElegir}
            className={cn(
              "flex min-h-[44px] w-full min-w-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-[15px] md:text-sm transition-colors",
              i === activa ? "bg-[#f4f9ff]" : "bg-transparent",
              "hover:bg-[#f4f9ff] active:bg-[#e3f0fc]",
              o.fija && "text-[#1d5fa8] font-semibold",
              o.fija && i > 0 && !opciones[i - 1].fija && "mt-1 border-t border-[#e3f0fc] rounded-t-none",
            )}
          >
            {o.contenido}
          </button>
        ))}
      </div>
      {pie}
    </div>
  );
}

/**
 * Las teclas del ordenador sobre la lista: ↑/↓ mueven la activa (dando la
 * vuelta), Enter elige la activa y Escape cierra. Devuelve true si la tecla era
 * suya; entonces se hace `preventDefault` para que Enter no envíe el formulario
 * (el `Input` de la app envía y cierra el teclado con Enter si nadie lo para).
 */
export function teclaLista(
  e: KeyboardEvent<HTMLInputElement>,
  { abierta, opciones, activa, setActiva, cerrar }: {
    abierta: boolean;
    opciones: OpcionLista[];
    activa: number;
    setActiva: (i: number) => void;
    cerrar: () => void;
  },
): boolean {
  if (e.key === "Escape") {
    if (!abierta) return false;
    e.preventDefault();
    cerrar();
    return true;
  }
  if (!abierta || opciones.length === 0) return false;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    setActiva(activa < 0 ? 0 : (activa + 1) % opciones.length);
    return true;
  }
  if (e.key === "ArrowUp") {
    e.preventDefault();
    setActiva(activa <= 0 ? opciones.length - 1 : activa - 1);
    return true;
  }
  if (e.key === "Enter" && activa >= 0 && activa < opciones.length) {
    e.preventDefault();
    opciones[activa].onElegir();
    return true;
  }
  return false;
}

/**
 * Cerrar la lista al tocar FUERA del selector, y no al perder el foco: en
 * Android el `blur` del campo llega antes que el toque en la fila y se lleva por
 * delante la elección (lo mismo que se aprendió en `RegistrySuggestions`). El
 * tabulador sí cierra: lo resuelve el `onBlur` del campo mirando `relatedTarget`.
 */
export function useCerrarAlTocarFuera(caja: RefObject<HTMLElement>, abierta: boolean, cerrar: () => void) {
  useEffect(() => {
    if (!abierta) return;
    const alTocar = (e: PointerEvent) => {
      if (caja.current && e.target instanceof Node && !caja.current.contains(e.target)) cerrar();
    };
    document.addEventListener("pointerdown", alTocar);
    return () => document.removeEventListener("pointerdown", alTocar);
  }, [caja, abierta, cerrar]);
}
